import { type EmployeeType } from '~/api/__generated-types__'
import { URL_PARAM_KEYS } from '~/constants'
import { findLatestMonday } from '~/utils/date'
import { cleanInt } from '~/utils/helpers'
import { getCaptchaToken } from '~/utils/recaptchaClient'

export const getStartDateFromUrl = (url: URL): Date => {
  const dateStr =
    url.searchParams.get(URL_PARAM_KEYS.date) ||
    // on confirm page, the date is passed as "start"
    url.searchParams.get(URL_PARAM_KEYS.start)
  const date = dateStr ? new Date(dateStr) : undefined
  return findLatestMonday(date && !isNaN(date.getTime()) ? date : new Date())
}

export const recaptchaPrecheckSubmit = async (action: string) => {
  try {
    // 1. Frontend early check
    const precheckToken = await getCaptchaToken(action)
    const precheck = await fetch('/api/recaptcha-precheck', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: precheckToken,
        action,
      }),
    }).then(r => r.json())

    if (!precheck.ok) {
      return false
    }
    // generate token for the actual form submission
    return await getCaptchaToken(action)
  } catch (err) {
    console.error('recaptcha error', err)
    return false
  }
}

export const checkIntParamAgainstOptions = <T extends { id: number }>(
  searchParams: URLSearchParams,
  paramName: string,
  options: T[],
): T | undefined => {
  const paramValue = cleanInt(searchParams.get(paramName))
  if (paramValue === undefined) {
    return undefined
  }
  return options.find(option => option.id === paramValue)
}
export const checkAllIntParamAgainstOptions = <T extends { id: number }>(
  searchParams: URLSearchParams,
  paramName: string,
  options: T[],
): T[] => {
  const intValues: number[] = searchParams
    .getAll(paramName)
    .reduce<number[]>((acc, next) => {
      const parsed = cleanInt(next)
      if (parsed) {
        acc.push(parsed)
      }
      return acc
    }, [])
  return options.filter(option => intValues.includes(option.id))
}

const strings = {
  allEmployees: 'Alle Mitarbeitenden',
  myAppointments: 'Meine Termine',
}

export type Option = {
  label: string
  value: number | string
}

export const getEmployeeOptionsSortedByName = (
  employees: EmployeeType[],
  myEmployeeId: number,
): Option[] => {
  const sortedEmployees = employees
    .slice(0)
    .sort((a, b) => a.fullName.localeCompare(b.fullName))
  return sortedEmployees.reduce(
    (options: Option[], employee) => {
      // Set the employee object of the current user to the second position of the options and replace the name with "Meine Termine"
      if (employee.id === myEmployeeId) {
        options.splice(1, 0, {
          label: strings.myAppointments,
          value: employee.id,
        })
      } else {
        options.push({ label: employee.fullName, value: employee.id })
      }
      return options
    },
    employees.length > 1 ? [{ label: strings.allEmployees, value: '' }] : [],
  )
}
